import type { Category, CategoryCheck } from "../api/category";
import type { Check } from "../api/check";
import {
  CHECK_TYPE_KEYS,
  type Pricelist,
} from "../api/pricelist";
import { CHECK_NAME_LABELS, labelForCheckName, resolveCheckNameKey } from "./checkLabels";
import {
  formatPricelistPrice,
  getLimitPeriodLabel,
  getPaymentPeriodLabel,
  getPricelistPriceItems,
} from "./pricelistFormat";

export interface LandingPricingPriceTier {
  amount: string;
  periodLabel: string;
  isPrimary?: boolean;
}

export interface LandingPricingCheckGroup {
  id: string;
  name: string;
  description?: string;
  checks: string[];
}

export interface LandingPricingPolicyCheck {
  id: string;
  label: string;
}

export interface LandingPricingPolicyLimit {
  period: string;
  periodLabel: string;
  value: string;
}

export interface LandingPricingPlan {
  id: string;
  title?: string;
  description?: string;
  prices: LandingPricingPriceTier[];
  checkGroups: LandingPricingCheckGroup[];
  policyCheckEnabled?: boolean;
  policyChecks: LandingPricingPolicyCheck[];
  policyLimits: LandingPricingPolicyLimit[];
  siteAmount?: number;
  emailNotification?: boolean;
  scanByScheduleEnabled?: boolean;
}

const PAYMENT_PERIOD_MARKETING: Record<string, string> = {
  month: "в месяц",
  quarter: "в квартал",
  year: "в год",
};

const getPaymentPeriodMarketingLabel = (period: string): string =>
  PAYMENT_PERIOD_MARKETING[period] ?? getPaymentPeriodLabel(period).toLowerCase();

const normalizeTextKey = (value: string): string =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

const getCheckDisplayLabel = (check: CategoryCheck | Check): string | null => {
  const description = check.description?.trim();
  if (description) return description;

  const code = check.code?.trim();
  if (code) return labelForCheckName(code) ?? code;

  return check.id?.trim() ?? null;
};

const getCheckIdentityKeys = (check: CategoryCheck, label: string): string[] => {
  const keys = new Set<string>();

  const id = check.id?.trim();
  if (id) keys.add(`id:${id}`);

  const code = check.code?.trim();
  const codeKey = code ? resolveCheckNameKey(code) : undefined;
  if (codeKey) keys.add(`code:${codeKey}`);

  const descriptionKey = check.description ? resolveCheckNameKey(check.description) : undefined;
  if (descriptionKey) keys.add(`code:${descriptionKey}`);

  for (const [key, text] of Object.entries(CHECK_NAME_LABELS)) {
    if (normalizeTextKey(text) === normalizeTextKey(label)) {
      keys.add(`code:${key}`);
    }
  }

  keys.add(`label:${normalizeTextKey(label)}`);

  return [...keys];
};

const resolveCheckGroups = (
  categoryIds: string[],
  categoryById: Map<string, Category>
): LandingPricingCheckGroup[] => {
  const groups: LandingPricingCheckGroup[] = [];

  for (const categoryId of categoryIds) {
    const category = categoryById.get(categoryId);
    if (!category) continue;

    const seenInGroup = new Set<string>();
    const checks: string[] = [];

    for (const check of category.checks ?? []) {
      const label = getCheckDisplayLabel(check);
      if (!label) continue;

      const identityKeys = getCheckIdentityKeys(check, label);
      if (identityKeys.some((key) => seenInGroup.has(key))) continue;

      for (const key of identityKeys) {
        seenInGroup.add(key);
      }
      checks.push(label);
    }

    if (checks.length === 0) continue;

    groups.push({
      id: categoryId,
      name: category.name?.trim() || "Категория",
      description: category.description?.trim() || undefined,
      checks,
    });
  }

  return groups;
};

const resolvePolicyChecks = (
  pricelist: Pricelist,
  checkById: Map<string, Check>
): LandingPricingPolicyCheck[] => {
  const items = pricelist.policyCheckSettings?.checks ?? [];

  return items.flatMap((check, index) => {
    const checkId = check.id?.trim();
    const fromList = checkId ? checkById.get(checkId) : undefined;
    const label =
      getCheckDisplayLabel(check) ||
      (fromList && getCheckDisplayLabel(fromList)) ||
      checkId;
    if (!label) return [];

    return [
      {
        id: checkId ?? `policy-check-${index}`,
        label,
      },
    ];
  });
};

const resolvePolicyLimits = (pricelist: Pricelist): LandingPricingPolicyLimit[] =>
  (pricelist.policyCheckSettings?.usageSettings ?? []).map((usage) => ({
    period: usage.period,
    periodLabel: getLimitPeriodLabel(usage.period),
    value: String(usage.limit),
  }));

export const buildLandingPricingPlans = (
  pricelists: Pricelist[],
  categories: Category[],
  checks: Check[] = []
): LandingPricingPlan[] => {
  const categoryById = new Map(
    categories.filter((category): category is Category & { id: string } => Boolean(category.id)).map(
      (category) => [category.id, category]
    )
  );
  const checkById = new Map(
    checks.filter((check): check is Check & { id: string } => Boolean(check.id)).map((check) => [
      check.id,
      check,
    ])
  );

  return pricelists
    .filter((item) => item.status === "active" && item.visibleForUsers !== false)
    .map((item, index) => {
      const sortedPrices = getPricelistPriceItems(item);
      const hasCheckTypes =
        item.checkTypes != null &&
        CHECK_TYPE_KEYS.some((key) => item.checkTypes?.[key]?.enabled === true);

      const policyCheckEnabled = hasCheckTypes
        ? item.checkTypes?.policyCheck?.enabled === true
        : item.policyCheckEnabled === true;
      const policyChecks =
        !hasCheckTypes && policyCheckEnabled ? resolvePolicyChecks(item, checkById) : [];
      const policyLimits =
        !hasCheckTypes && policyCheckEnabled ? resolvePolicyLimits(item) : [];

      const categoryIds = hasCheckTypes
        ? [
            ...new Set(
              CHECK_TYPE_KEYS.flatMap((key) => {
                const config = item.checkTypes?.[key];
                if (!config?.enabled) return [];
                return config.categoryIds ?? [];
              }).filter((categoryId): categoryId is string => Boolean(categoryId))
            ),
          ]
        : policyCheckEnabled
          ? []
          : [
              ...new Set(
                (item.tariffItemList ?? [])
                  .map((tariff) => tariff.categoryId)
                  .filter((categoryId): categoryId is string => Boolean(categoryId))
              ),
            ];
      const checkGroups = resolveCheckGroups(categoryIds, categoryById);

      const hasMonthly = sortedPrices.some((price) => price.period === "month");
      const prices: LandingPricingPriceTier[] = sortedPrices.map((priceItem, priceIndex) => ({
        amount: formatPricelistPrice(priceItem.price),
        periodLabel: getPaymentPeriodMarketingLabel(priceItem.period),
        isPrimary: priceItem.period === "month" || (!hasMonthly && priceIndex === 0),
      }));

      const emailNotification = hasCheckTypes
        ? CHECK_TYPE_KEYS.some((key) => item.checkTypes?.[key]?.options?.report === true)
        : item.emailNotification === true;
      const scanByScheduleEnabled = hasCheckTypes
        ? CHECK_TYPE_KEYS.some((key) => item.checkTypes?.[key]?.options?.schedule === true)
        : item.scanByScheduleEnabled === true;

      return {
        id: item.id ?? `plan-${index}`,
        title: item.name?.trim() || undefined,
        description: item.description?.trim() || undefined,
        prices,
        checkGroups,
        policyCheckEnabled,
        policyChecks,
        policyLimits,
        siteAmount: hasCheckTypes
          ? (() => {
              const config = item.checkTypes?.siteCheck;
              const limits = Array.isArray(config?.limits) ? config?.limits ?? [] : [];
              const readCount = (entry: { count?: number; limit?: number }) => {
                const count = Number(entry.count);
                if (Number.isFinite(count) && count > 0) return count;
                const legacy = Number(entry.limit);
                return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
              };
              const month = limits.find((entry) => entry.period === "month");
              if (month) {
                const value = readCount(month as { count?: number; limit?: number });
                if (value > 0) return value;
              }
              const max = limits.reduce((acc, entry) => {
                const value = readCount(entry as { count?: number; limit?: number });
                return value > acc ? value : acc;
              }, 0);
              if (max > 0) return max;
              const legacy = Number((config as { limit?: number } | undefined)?.limit);
              return Number.isFinite(legacy) && legacy > 0 ? legacy : undefined;
            })()
          : item.siteAmount,
        emailNotification,
        scanByScheduleEnabled,
      };
    })
    .filter(
      (plan) =>
        plan.prices.length > 0 ||
        plan.checkGroups.length > 0 ||
        plan.policyChecks.length > 0
    );
};
